import { Button, ButtonProps, Link, Typography } from "@mui/material";
import { CoreIconButton, CoreIconButtonProps } from "../core/button/Button";
import styles from "./QuickSetupGuide.module.css";
import i18nPackage from "../i18n/i18n";
import ThemeWrapper from "../theme";
import { ComponentPropsWithoutRef, useMemo, useRef, useState, forwardRef, useEffect } from "react";
import {
	CoreCheckmarkIcon,
	CoreChip,
	CoreExpandIcon,
	CoreFullScreenIcon,
	CoreInfoIcon,
	CoreRetryIcon,
	CoreSpinner,
	CoreXIcon,
} from "../core";
import { Trans } from "react-i18next";
import Draggable from "react-draggable";
import MinimizeIcon from "../images/minimize.svg?react";
import { IntegrationConfigPayload } from "./quickSetupGuideHelper";
import { ConditionalWrapper } from "../wrapper";
import { SETUP_GUIDE_ATTRIBUTES, IntegrationType } from "./setupGuideConstants";

type SuppressedEditQuizIframe = { el: HTMLIFrameElement; prevPointerEvents: string };

function restoreEditQuizIframePointerEvents(ref: React.MutableRefObject<SuppressedEditQuizIframe | null>) {
	if (!ref.current) return;
	ref.current.el.style.pointerEvents = ref.current.prevPointerEvents;
	ref.current = null;
}

type ActionButtonProps = CoreIconButtonProps & {
	minimized: boolean;
};

const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(function ActionButton(
	{ children, minimized, ...buttonProps },
	ref,
) {
	return (
		<CoreIconButton
			ref={ref}
			{...buttonProps}
			variant="plain"
			slotProps={{
				tooltip: {
					placement: minimized ? "top" : "bottom",
				},
			}}
			className={`${styles.actionButton} ${minimized ? styles.minimized : styles.full}`}
		>
			{children}
		</CoreIconButton>
	);
});

function StatusIcon({ completed }: { completed?: boolean }) {
	return (
		<div className={`${styles.statusIcon} ${completed ? styles.completed : styles.notComplete}`}>
			{completed && <CoreCheckmarkIcon color="#fff" height={14} width={14} />}
		</div>
	);
}

type IntegrationButtonWrapperProps = {
	isButton?: boolean;
	isSelected?: boolean;
	children: React.ReactNode;
	buttonProps?: Omit<ButtonProps, "children">;
	divProps?: Omit<ComponentPropsWithoutRef<"div">, "children">;
};

function IntegrationButtonWrapper({
	isButton,
	isSelected,
	children,
	buttonProps,
	divProps,
}: IntegrationButtonWrapperProps) {
	const className = `${styles.integrationButton} ${isSelected ? styles.selected : undefined}`;

	return (
		<ConditionalWrapper
			condition={Boolean(isButton)}
			whenFalse={(children) => (
				<div {...divProps} className={className}>
					{children}
				</div>
			)}
			whenTrue={(children) => (
				<Button {...buttonProps} className={`${className} ${buttonProps?.className || ""}`}>
					{children}
				</Button>
			)}
		>
			{children}
		</ConditionalWrapper>
	);
}

type IntegrationButtonHeaderProps = {
	label?: React.ReactNode;
	subtitle?: string;
	completed?: boolean;
	children?: React.ReactNode;
	showStatusIcon?: boolean;
};

function IntegrationButtonHeader({
	label,
	subtitle,
	completed,
	children,
	showStatusIcon = true,
}: IntegrationButtonHeaderProps) {
	return (
		<div className={styles.integrationButtonHeader}>
			<div className={styles.integrationButtonTitle}>
				{children}
				{subtitle && <Typography className={styles.subtitle}>{subtitle}</Typography>}
				{label && (typeof label === "string" ? <Typography className={styles.label}>{label}</Typography> : label)}
			</div>
			{showStatusIcon && <StatusIcon completed={completed} />}
		</div>
	);
}

type ExpandableSectionProps = {
	isExpanded: boolean;
	subtitle: string;
	onClick: () => void;
	children?: React.ReactNode;
};

function ExpandableSection({ isExpanded, subtitle, onClick, children }: ExpandableSectionProps) {
	return (
		<div className={styles.expandableSection}>
			<IntegrationButtonWrapper isButton={true} buttonProps={{ onClick, className: styles.expandableButton }}>
				<IntegrationButtonHeader subtitle={subtitle} showStatusIcon={!isExpanded} completed={!isExpanded}>
					<CoreExpandIcon expanded={isExpanded} variant="narrow" />
				</IntegrationButtonHeader>
			</IntegrationButtonWrapper>
			{isExpanded && <div className={styles.expandableContent}>{children}</div>}
		</div>
	);
}

type IntegrationButtonProps = {
	label?: string;
	completed: boolean;
	selected: boolean;
	description?: string;
	buttonText?: string;
	subtitle?: string;
	onClick: () => void;
	clickable?: boolean;
};

function IntegrationButton({
	label,
	completed,
	selected,
	description,
	buttonText,
	subtitle,
	onClick,
	clickable = true,
}: IntegrationButtonProps) {
	return (
		<IntegrationButtonWrapper isButton={Boolean(clickable)} buttonProps={{ onClick }} isSelected={selected}>
			<IntegrationButtonHeader subtitle={subtitle} completed={completed} label={label} />

			{(description || buttonText) && (
				<>
					{description && <Typography className={styles.buttonDescription}>{description}</Typography>}
					{buttonText && (
						<Button className={styles.integrationAction} onClick={onClick}>
							{buttonText}
						</Button>
					)}
				</>
			)}
		</IntegrationButtonWrapper>
	);
}

type IntegrationConfig = {
	title: string;
	description?: string;
	buttonText?: string;
};

const INTEGRATION_CONFIGS: Record<IntegrationType, IntegrationConfig> = {
	"LTI 1.3": {
		title: i18nPackage.t("quickSetupGuide.integrations.1p3"),
	},
	"LMS Visualizer": {
		title: i18nPackage.t("quickSetupGuide.integrations.visualizer"),
	},
	"LMS API": {
		title: i18nPackage.t("quickSetupGuide.integrations.api"),
	},
	"Create Profile": {
		title: i18nPackage.t("quickSetupGuide.integrations.profile"),
		description: i18nPackage.t("quickSetupGuide.integrations.profileDesc"),
		buttonText: i18nPackage.t("quickSetupGuide.integrations.profileButton"),
	},
	"Browser Extension": {
		title: i18nPackage.t("quickSetupGuide.integrations.extension"),
		description: i18nPackage.t("quickSetupGuide.integrations.extensionDesc"),
		buttonText: i18nPackage.t("quickSetupGuide.integrations.extensionButton"),
	},
	"Create Assessment": {
		title: i18nPackage.t("quickSetupGuide.integrations.createAssessment"),
		description: i18nPackage.t("quickSetupGuide.integrations.createAssessmentDesc"),
		buttonText: i18nPackage.t("quickSetupGuide.integrations.createAssessmentButton"),
	},
	"Take Assessment": {
		title: i18nPackage.t("quickSetupGuide.integrations.takeAssessment"),
		description: i18nPackage.t("quickSetupGuide.integrations.takeAssessmentDesc"),
		buttonText: i18nPackage.t("quickSetupGuide.integrations.takeAssessmentButton"),
	},
	"View Results": {
		title: i18nPackage.t("quickSetupGuide.integrations.viewResults"),
		description: i18nPackage.t("quickSetupGuide.integrations.viewResultsDesc"),
		buttonText: i18nPackage.t("quickSetupGuide.integrations.viewResultsButton"),
	},
};

type GuideBodyProps = {
	onMinimizeChange: () => void;
	minimized: boolean;
} & Pick<
	QuickSetupGuideProps,
	"integrationConfig" | "onIntegrationClick" | "selectedIntegration" | "onClose" | "onRefresh"
>;

function GuideBody({
	onMinimizeChange,
	onClose,
	minimized,
	integrationConfig,
	onIntegrationClick,
	selectedIntegration,
	onRefresh,
}: GuideBodyProps) {
	const [expandCompleted, setExpandCompleted] = useState(false);
	const [isRefreshing, setIsRefreshing] = useState(false);

	const configs = useMemo(() => {
		let allPreviousCompleted = true;

		return integrationConfig.array.map((config) => {
			// A step can only show true completion if it is completed AND its predecessors are done
			const visuallyCompleted = config.completed && allPreviousCompleted;

			// If this step is incomplete, break the chain for all steps further down the array line
			if (!config.completed) {
				allPreviousCompleted = false;
			}

			return {
				...config,
				completed: visuallyCompleted,
			};
		});
	}, [integrationConfig.array]);

	const completedConfigs = configs.filter((config) => config.completed);
	const incompleteConfigs = configs.filter((config) => !config.completed);
	const numCompleted = completedConfigs.length;
	const total = configs.length;
	const allCompleted = numCompleted === total;

	// Get FIRST incomplete item's index inside the ORIGINAL full array
	const firstIncompleteOriginalIndex = configs.findIndex((config) => !config.completed);

	const completedSubtitle = useMemo(() => {
		if (expandCompleted) {
			return i18nPackage.t("quickSetupGuide.collapseCompleted");
		}
		if (numCompleted === 0) return "";
		if (numCompleted === 1) {
			return i18nPackage.t("quickSetupGuide.stepNumber", { number: 1 });
		}
		return i18nPackage.t("quickSetupGuide.stepNumberProgress", {
			firstNum: 1,
			lastNum: numCompleted,
		});
	}, [numCompleted, expandCompleted]);

	const handleRefresh = async () => {
		if (isRefreshing || !onRefresh) return;
		try {
			setIsRefreshing(true);
			await onRefresh();
		} finally {
			setIsRefreshing(false);
		}
	};

	return (
		<>
			<div className={styles.guideHeader}>
				<Typography className={styles.guideTitle}>
					{minimized ? i18nPackage.t("quickSetupGuide.setupGuide") : i18nPackage.t("quickSetupGuide.title")}
				</Typography>
				<div className={styles.actionButtonContainer}>
					{!minimized && onRefresh && (
						<ActionButton
							minimized={minimized}
							tooltip={i18nPackage.t("quickSetupGuide.refresh")}
							onClick={handleRefresh}
							style={{
								cursor: isRefreshing ? "wait" : "pointer",
							}}
						>
							{isRefreshing ? <CoreSpinner size={16} /> : <CoreRetryIcon width={16} height={16} />}
						</ActionButton>
					)}
					<ActionButton
						minimized={minimized}
						tooltip={minimized ? i18nPackage.t("quickSetupGuide.expand") : i18nPackage.t("quickSetupGuide.minimize")}
						onClick={onMinimizeChange}
					>
						{minimized ? <CoreFullScreenIcon /> : <MinimizeIcon />}
					</ActionButton>
					<ActionButton minimized={minimized} tooltip={i18nPackage.t("quickSetupGuide.close")} onClick={onClose}>
						<CoreXIcon />
					</ActionButton>
				</div>
			</div>
			{!minimized && (
				<div className={styles.guideBody}>
					<Typography fontSize="14px">
						{allCompleted ? i18nPackage.t("quickSetupGuide.allComplete") : i18nPackage.t("quickSetupGuide.bodyPrompt")}
					</Typography>
					<div className={styles.progressBarSection}>
						<div className={styles.progressBarHeader}>
							<Typography className={styles.progressBarTitle}>
								{i18nPackage.t("quickSetupGuide.progressBarTitle")}
							</Typography>
							<CoreChip
								className={styles.progressBarChip}
								chipColor="#F2EEFF"
								fontColor="#5B45A4"
								variant="rectangular"
							>
								{i18nPackage.t("quickSetupGuide.progressBarLabel", { completed: numCompleted, total })}
							</CoreChip>
						</div>
						<div className={styles.progressBar}>
							<div className={styles.progress} style={{ width: `${(numCompleted / total) * 100}%` }} />
						</div>
					</div>
					<div className={styles.guideIntegrations}>
						{numCompleted > 0 && (
							<>
								<ExpandableSection
									subtitle={completedSubtitle}
									onClick={() => setExpandCompleted((prev) => !prev)}
									isExpanded={expandCompleted}
								>
									{completedConfigs.map((config) => {
										// Find absolute positioning inside the master configuration map list
										const originalIndex = configs.findIndex((c) => c.type === config.type);
										const absoluteStepNumber = originalIndex + 1;
										const { title } = INTEGRATION_CONFIGS[config.type];
										const { disabled } = config;

										return (
											<IntegrationButtonHeader
												key={config.type}
												label={
													disabled ? (
														title
													) : (
														<Link
															className={styles.label}
															component="button"
															onClick={() => onIntegrationClick?.(config.type)}
														>
															{title}
														</Link>
													)
												}
												subtitle={i18nPackage.t("quickSetupGuide.stepNumber", { number: absoluteStepNumber })}
												showStatusIcon={false}
											/>
										);
									})}
								</ExpandableSection>
							</>
						)}
						{incompleteConfigs.map((config) => {
							const { type, completed, disabled } = config;
							const { title, description, buttonText } = INTEGRATION_CONFIGS[type];
							const selected = selectedIntegration === type;

							const originalIndex = configs.findIndex((c) => c.type === type);
							const absoluteStepNumber = originalIndex + 1;

							// Check if this specific item is the absolute first incomplete task row
							const isFirstIncomplete = originalIndex === firstIncompleteOriginalIndex;

							return (
								<IntegrationButton
									key={type}
									label={title}
									subtitle={i18nPackage.t("quickSetupGuide.stepNumber", { number: absoluteStepNumber })}
									completed={completed}
									selected={selected}
									onClick={() => onIntegrationClick?.(type)}
									// Only show description and button if it's the first incomplete
									description={isFirstIncomplete ? description : undefined}
									buttonText={isFirstIncomplete && !disabled ? buttonText : undefined}
									clickable={isFirstIncomplete && !buttonText && !disabled}
								/>
							);
						})}
					</div>
					<div className={styles.footer}>
						<CoreInfoIcon width={14} height={14} />
						<Trans
							i18n={i18nPackage}
							i18nKey="quickSetupGuide.footerLink"
							components={{
								a: <Link href="https://support.yuja.com/" target="_blank" rel="noreferrer" fontSize="12px" />,
							}}
						/>
					</div>
				</div>
			)}
		</>
	);
}

type QuickSetupGuideProps = {
	onClose: () => void;
	integrationConfig: IntegrationConfigPayload;
	onIntegrationClick?: (type: IntegrationType) => void;
	selectedIntegration?: IntegrationType;
	initialPosition?: { x: number; y: number };
	onPositionChange?: (position: { x: number; y: number }) => void;
	onReady?: (element: HTMLElement) => void;
	initialMinimized?: boolean;
	onMinimizedChange?: (minimized: boolean) => void;
	onRefresh?: () => Promise<void>;
};

export default function QuickSetupGuide({
	onClose,
	integrationConfig,
	onIntegrationClick,
	selectedIntegration,
	initialPosition,
	onPositionChange,
	onReady,
	initialMinimized,
	onMinimizedChange,
	onRefresh,
}: QuickSetupGuideProps) {
	const [position, setPosition] = useState<{ x: number; y: number }>(() => {
		if (initialPosition) {
			return initialPosition;
		}

		const componentWidth = initialMinimized ? 165 : 340;
		const componentHeight = initialMinimized ? 42 : 350;

		//  from this file's .guideWrapper CSS offsets (QuickSetupGuide.module.css):
		// top: 50px, right: 36px, left: 70px, bottom: 20px
		// bottom intentionally differs from frontend/verity's original index.module.css (which uses bottom: 0) because the UI team wanted some bottom padding.
		const availableWidth = window.innerWidth - 70 - 36;
		const availableHeight = window.innerHeight - 50 - 20;

		const defaultX = Math.max(0, availableWidth - componentWidth);
		const defaultY = Math.max(0, availableHeight - componentHeight);

		return { x: defaultX, y: defaultY };
	});
	const [minimized, setMinimized] = useState(!!initialMinimized || false);
	const draggableCardRef = useRef<HTMLDivElement | null>(null);
	const suppressedEditQuizIframeRef = useRef<SuppressedEditQuizIframe | null>(null);

	const handleMinimizeChange = () => {
		const nextMinimized = !minimized;

		// Check if opening guide would be out of bounds
		if (!nextMinimized && draggableCardRef.current) {
			const parent = draggableCardRef.current.parentElement;
			if (parent) {
				const parentRect = parent.getBoundingClientRect();

				const expandedHeight = 350;
				const maxAllowedY = parentRect.height - expandedHeight;

				let targetY = position.y;
				if (targetY > maxAllowedY) {
					targetY = Math.max(0, maxAllowedY);
				} else if (targetY < 0) {
					targetY = 0;
				}

				if (targetY !== position.y) {
					const adjustedPosition = {
						x: position.x,
						y: targetY,
					};
					setPosition(adjustedPosition);
					onPositionChange?.(adjustedPosition);
				}
			}
		}
		setMinimized(nextMinimized);
		onMinimizedChange?.(nextMinimized);
	};

	useEffect(() => {
		if (initialPosition) {
			setPosition(initialPosition);
		}
	}, [initialPosition]);

	useEffect(() => {
		return () => {
			// Guard against unmounting mid-drag (e.g. onClose during a drag), which would otherwise
			// skip onStop and leave the edit-quiz iframe stuck with pointer-events: none.
			restoreEditQuizIframePointerEvents(suppressedEditQuizIframeRef);
		};
	}, []);

	return (
		<ThemeWrapper>
			<div className={styles.guideContainer} style={{ zIndex: SETUP_GUIDE_ATTRIBUTES.Z_INDEX.GUIDE }}>
				<div className={styles.guideWrapper}>
					<Draggable
						nodeRef={draggableCardRef}
						bounds="parent"
						handle={`.${styles.guideHeader}`}
						position={position}
						enableUserSelectHack={true}
						onStart={() => {
							// The edit-quiz modal renders inside a full-viewport iframe with pointer-events
							// enabled. If a fast drag moves the cursor over it, mousemove/mouseup land in that iframe's
							// own document instead of this one, so react-draggable stops tracking the drag (feels
							// laggy, or gets "stuck" following the pointer if the mouseup is lost). Disable pointer
							// events on it for the duration of the drag so this document keeps receiving every event.
							const iframe = document.getElementById(SETUP_GUIDE_ATTRIBUTES.ID.EDIT_QUIZ_IFRAME);
							// Skip if already suppressed: a re-entrant onStart (a prior drag's onStop never ran)
							// would otherwise capture "none" as prevPointerEvents and lose the real original value.
							if (iframe instanceof HTMLIFrameElement && !suppressedEditQuizIframeRef.current) {
								suppressedEditQuizIframeRef.current = { el: iframe, prevPointerEvents: iframe.style.pointerEvents };
								iframe.style.pointerEvents = "none";
							}
						}}
						onDrag={(_, data) => {
							const newPosition = { x: data.x, y: data.y };
							setPosition(newPosition);
						}}
						onStop={(_, data) => {
							restoreEditQuizIframePointerEvents(suppressedEditQuizIframeRef);
							const finalPosition = { x: data.x, y: data.y };
							onPositionChange?.(finalPosition);
						}}
					>
						<div
							ref={(node) => {
								if (node) {
									draggableCardRef.current = node;
									onReady?.(node);
								} else {
									draggableCardRef.current = null;
								}
							}}
							className={`${styles.guide} ${minimized ? styles.minimized : styles.full}`}
						>
							<GuideBody
								onClose={onClose}
								minimized={minimized}
								onMinimizeChange={handleMinimizeChange}
								integrationConfig={integrationConfig}
								onIntegrationClick={onIntegrationClick}
								selectedIntegration={selectedIntegration}
								onRefresh={onRefresh}
							/>
						</div>
					</Draggable>
				</div>
			</div>
		</ThemeWrapper>
	);
}
